import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCcw, TrendingDown, TrendingUp, Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";

type GrowthPayload = {
  this_month_start: string;
  last_month_start: string;
  this_month_new_customers: number;
  last_month_new_customers: number;
  month_over_month_change: number;
  month_over_month_change_pct: number;
};

type DebitRow = {
  month_start: string;
  authorized: boolean;
  total_debit: number;
};

type RebateRow = {
  month_start: string;
  authorized: boolean;
  total_rebate: number;
};

type EarnAnalyticsOverviewResponse = {
  months: number;
  timezone: string;
  customer_growth: GrowthPayload;
  debit_by_month_authorized: DebitRow[];
  rebate_by_month_authorized: RebateRow[];
};

type ChartRow = {
  month: string;
  authorizedDebit: number;
  unauthorizedDebit: number;
  authorizedRebate: number;
  unauthorizedRebate: number;
};

const overviewChartConfig = {
  authorizedDebit: { label: "Allocated (Authorized)", color: "var(--chart-1)" },
  unauthorizedDebit: { label: "Not Allocated (Unauthorized)", color: "var(--chart-2)" },
  authorizedRebate: { label: "Rebated (Authorized)", color: "var(--chart-3)" },
  unauthorizedRebate: { label: "Not Rebated (Unauthorized)", color: "var(--chart-4)" },
} satisfies ChartConfig;

function fmtNum(value: number | null | undefined) {
  const x = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return x.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtMonthLabel(monthStart: string) {
  const dt = new Date(`${monthStart}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return monthStart;
  return dt.toLocaleString(undefined, { month: "short", year: "numeric" });
}

export function EarnAnalyticsPage() {
  const [months, setMonths] = useState<string>("3");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<EarnAnalyticsOverviewResponse | null>(null);

  const loadOverview = async (monthsWindow: string) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ months: monthsWindow });
      const resp = await fetch(`/v1/api/earn-code-manager/earn-analytics/overview/?${qs.toString()}`, {
        method: "GET",
      });
      const contentType = resp.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      const data = isJson ? await resp.json() : null;
      if (!resp.ok) {
        if (data && typeof data === "object" && "detail" in data) {
          throw new Error(String((data as { detail?: string }).detail || `Request failed (${resp.status})`));
        }
        const raw = await resp.text();
        throw new Error(
          raw?.trim()
            ? `Request failed (${resp.status}): ${raw.slice(0, 160)}`
            : `Request failed (${resp.status})`
        );
      }
      if (!data) {
        throw new Error(`Request succeeded but returned non-JSON payload (${resp.status}).`);
      }
      setOverview(data as EarnAnalyticsOverviewResponse);
    } catch (err) {
      setOverview(null);
      setError(err instanceof Error ? err.message : "Failed to load Earn Analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview(months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const chartRows = useMemo<ChartRow[]>(() => {
    if (!overview) return [];
    const map = new Map<string, ChartRow>();
    for (const row of overview.debit_by_month_authorized || []) {
      const key = String(row.month_start);
      const current = map.get(key) || {
        month: fmtMonthLabel(key),
        authorizedDebit: 0,
        unauthorizedDebit: 0,
        authorizedRebate: 0,
        unauthorizedRebate: 0,
      };
      if (row.authorized) {
        current.authorizedDebit = Number(row.total_debit || 0);
      } else {
        current.unauthorizedDebit = Number(row.total_debit || 0);
      }
      map.set(key, current);
    }
    for (const row of overview.rebate_by_month_authorized || []) {
      const key = String(row.month_start);
      const current = map.get(key) || {
        month: fmtMonthLabel(key),
        authorizedDebit: 0,
        unauthorizedDebit: 0,
        authorizedRebate: 0,
        unauthorizedRebate: 0,
      };
      if (row.authorized) {
        current.authorizedRebate = Number(row.total_rebate || 0);
      } else {
        current.unauthorizedRebate = Number(row.total_rebate || 0);
      }
      map.set(key, current);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map((entry) => entry[1]);
  }, [overview]);

  const totals = useMemo(() => {
    const growth = overview?.customer_growth;
    const thisMonth = Number(growth?.this_month_new_customers || 0);
    const lastMonth = Number(growth?.last_month_new_customers || 0);
    const change = Number(growth?.month_over_month_change || 0);
    const changePct = Number(growth?.month_over_month_change_pct || 0);
    const authorizedDebit = chartRows.reduce((sum, row) => sum + (row.authorizedDebit || 0), 0);
    const unauthorizedDebit = chartRows.reduce((sum, row) => sum + (row.unauthorizedDebit || 0), 0);
    const authorizedRebate = chartRows.reduce((sum, row) => sum + (row.authorizedRebate || 0), 0);
    const unauthorizedRebate = chartRows.reduce((sum, row) => sum + (row.unauthorizedRebate || 0), 0);
    return {
      thisMonth,
      lastMonth,
      change,
      changePct,
      authorizedDebit,
      unauthorizedDebit,
      authorizedRebate,
      unauthorizedRebate,
    };
  }, [overview, chartRows]);

  const isPositiveChange = totals.change >= 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>Earn Analytics</CardTitle>
            {/* <CardDescription>Customer growth, allocated earn quota, and rebate movement by authorization status.</CardDescription> */}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Window</span>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="h-9 font-normal" onClick={() => void loadOverview(months)} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Earn analytics failed to load</AlertTitle>
          <AlertDescription className="break-words">{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading || !overview ? (
          <>
            <Skeleton className="h-[112px]" />
            <Skeleton className="h-[112px]" />
            <Skeleton className="h-[112px]" />
            <Skeleton className="h-[112px]" />
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>New customers (this month)</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  {fmtNum(totals.thisMonth)}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  Last month: {fmtNum(totals.lastMonth)}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Month-over-month change</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  {isPositiveChange ? (
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  )}
                  {totals.change >= 0 ? "+" : ""}
                  {fmtNum(totals.change)}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant={isPositiveChange ? "default" : "secondary"} className="font-normal">
                  {totals.changePct >= 0 ? "+" : ""}
                  {fmtNum(totals.changePct)}%
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Allocated earn quota (authorized)</CardDescription>
                <CardTitle className="text-2xl">{fmtNum(totals.authorizedDebit)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  Unauthorized: {fmtNum(totals.unauthorizedDebit)}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Rebate amount (authorized)</CardDescription>
                <CardTitle className="text-2xl">{fmtNum(totals.authorizedRebate)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="font-normal">
                  Unauthorized: {fmtNum(totals.unauthorizedRebate)}
                </Badge>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Earn Debit by Month</CardTitle>
            <CardDescription>Allocated vs not allocated earn quota across monthly buckets.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading || !overview ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <ChartContainer config={overviewChartConfig} className="h-[300px] w-full">
                <BarChart data={chartRows} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} width={50} />
                  <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="authorizedDebit" fill="var(--color-authorizedDebit)" radius={4} />
                  <Bar dataKey="unauthorizedDebit" fill="var(--color-unauthorizedDebit)" radius={4} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rebate by Month</CardTitle>
            <CardDescription>Rebated vs not rebated amounts where previous quota exceeds new quota.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading || !overview ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <ChartContainer config={overviewChartConfig} className="h-[300px] w-full">
                <BarChart data={chartRows} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} width={50} />
                  <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="authorizedRebate" fill="var(--color-authorizedRebate)" radius={4} />
                  <Bar dataKey="unauthorizedRebate" fill="var(--color-unauthorizedRebate)" radius={4} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Breakdown</CardTitle>
          <CardDescription>
            Month buckets are aligned to the first day of month ({overview?.timezone || "Asia/Kuala_Lumpur"}).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading || !overview ? (
            <Skeleton className="h-[260px] w-full" />
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead className="text-right">Not Allocated</TableHead>
                    <TableHead className="text-right">Rebated</TableHead>
                    <TableHead className="text-right">Not Rebated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chartRows.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell className="font-medium">{row.month}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtNum(row.authorizedDebit)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtNum(row.unauthorizedDebit)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtNum(row.authorizedRebate)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtNum(row.unauthorizedRebate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
